/**
 * Get the range of items for pagination
 * This function calculates the start and end index for a given page and items per page.
 * It returns a string formatted as "startIndex, endIndex".
 *
 * @param {number} page - The current page number (0-based index).
 * @param {number} itemsPerPage - The number of items per page.
 * @return {string} - A string representing the range of items for pagination.
 **/

export function getItemRange(page, itemsPerPage) {
    // Calculate the start and end index for the given page
    const startIndex = page * itemsPerPage;

    // Ensure the endIndex does not exceed the total number of items
    const endIndex = startIndex + itemsPerPage;

    // Return the range as a string
    return `${startIndex}, ${endIndex - 1}`;
}

/**
 * Range string that requests every item for the current result set.
 * Used for export operations to fetch all records.
 *
 * @param {number} totalItems - Total number of items to export
 * @returns {string} Range string formatted as "0, totalItems-1"
 *
 * @example
 * getAllItemsRange(100) // Returns "0, 99"
 * getAllItemsRange(0)   // Returns "0, 0"
 */
export function getAllItemsRange(totalItems) {
    if (!totalItems || totalItems <= 0) {
        return '0, 0';
    }
    return `0, ${totalItems - 1}`;
}

/**
 * Derive consistent pagination fields from a Spring Data Page JSON body.
 * Normalizes API response to ensure consistent pagination state.
 *
 * @param {object|null|undefined} response - Parsed JSON body (not a raw array)
 * @param {number} [itemsPerPageFallback=10] - Fallback page size if not in response
 * @returns {{ totalPages: number, totalElements: number, currentPage: number, pageSize: number }|null}
 *
 * @example
 * const normalized = normalizeSpringDataPage(apiResponse, 20);
 * // { totalPages: 5, totalElements: 100, currentPage: 0, pageSize: 20 }
 */
export function normalizeSpringDataPage(response, itemsPerPageFallback = 10) {
    if (!response || Array.isArray(response)) {
        return null;
    }

    const totalElements = response.totalElements ?? 0;
    const pageSize = response.pageable?.pageSize ?? itemsPerPageFallback;

    if (pageSize <= 0) {
        return null;
    }

    // Calculate total pages using ceil to match API behavior
    const totalPages = totalElements === 0 ? 0 : Math.ceil(totalElements / pageSize);

    // Ensure current page is within valid range
    let currentPage = response.pageable?.pageNumber ?? 0;
    if (totalPages === 0) {
        currentPage = 0;
    } else {
        currentPage = Math.min(Math.max(0, currentPage), totalPages - 1);
    }

    return { totalPages, totalElements, currentPage, pageSize };
}
